import { memo, type ReactElement } from "react";
import { Check, CircleDot, Clock } from "lucide-react";
import { space } from "@tokens/index";
import { Badge, Text } from "../../primitives";
import { classifyTaskStatus, taskStatusLabel, type TaskStatus } from "../taskStatus";
import type { WorkItem } from "../../../experience-shell/workflows";
import { formatRelativeTime, formatShortDueDate, prettifyTaskType, statusDotColor } from "./formatters";
import { StatusDot } from "./shared";
import { RailRow } from "./RailRow";
import type { RailSelection } from "./types";

function StatusCaption({
  item,
  status,
  now,
}: {
  readonly item: WorkItem;
  readonly status: TaskStatus;
  readonly now: Date;
}): ReactElement {
  const accent = statusDotColor(status);
  const glyph: ReactElement = (() => {
    switch (status) {
      case "open":
        return <StatusDot tone={accent} />;
      case "pending":
        return <StatusDot tone={accent} hollow />;
      case "pending-approval":
        return <Clock size={11} strokeWidth={2.25} aria-hidden="true" />;
      case "completed":
        return <Check size={11} strokeWidth={2.5} aria-hidden="true" />;
    }
  })();

  const suffix: string = (() => {
    if (status === "pending" && item.dueDate !== null) {
      return ` · due ${formatShortDueDate(item.dueDate, now)}`;
    }
    if (status === "completed" && item.completedAt !== null) {
      return ` · ${formatRelativeTime(item.completedAt)}`;
    }
    return "";
  })();

  return (
    // The caption stays whole; the preview after it is the one part of line
    // two that truncates (see RailRow).
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: space[1], color: accent, flexShrink: 0, whiteSpace: "nowrap" }}
    >
      {glyph}
      <Text role="caption" size="xs" weight="medium" style={{ color: "inherit" }}>
        {taskStatusLabel(status)}
        {suffix}
      </Text>
    </span>
  );
}

export interface TaskRailEntryProps {
  readonly item: WorkItem;
  readonly selected: boolean;
  readonly now: Date;
  readonly onSelectItem: (id: RailSelection) => void;
}

/** The `now`-derived parts of a task row: status bucket and the due suffix. */
function taskTimeFingerprint(item: WorkItem, now: Date): string {
  const status = classifyTaskStatus(item, now);
  const due = status === "pending" && item.dueDate !== null ? formatShortDueDate(item.dueDate, now) : "";
  return `${status}|${due}`;
}

// The board clock ticks every 30s for every row. Only rows whose status or
// due label changes re-render on a tick.
function taskRailEntryPropsEqual(prev: TaskRailEntryProps, next: TaskRailEntryProps): boolean {
  return (
    prev.item === next.item &&
    prev.selected === next.selected &&
    prev.onSelectItem === next.onSelectItem &&
    (prev.now === next.now || taskTimeFingerprint(prev.item, prev.now) === taskTimeFingerprint(next.item, next.now))
  );
}

export const TaskRailEntry = memo(function TaskRailEntry({
  item,
  selected,
  now,
  onSelectItem,
}: TaskRailEntryProps): ReactElement {
  const status = classifyTaskStatus(item, now);
  const accent = statusDotColor(status);
  return (
    <RailRow
      label={item.taskType != null && item.taskType.length > 0 ? prettifyTaskType(item.taskType) : "Task"}
      sublabel={item.body}
      icon={<CircleDot size={15} strokeWidth={2.25} aria-hidden="true" />}
      selected={selected}
      onSelect={() => onSelectItem(item.id)}
      accent={accent}
      edgeBarColor={accent}
      caption={<StatusCaption item={item} status={status} now={now} />}
      trailing={
        item.childWorkflowCount > 0 ? (
          <Badge variant="neutral" size="sm">
            {String(item.childWorkflowCount)}
          </Badge>
        ) : undefined
      }
    />
  );
}, taskRailEntryPropsEqual);
